import { useQuery } from '@tanstack/react-query';

import {
	getOperatorTicketDetailOptions,
	type OperatorTicketDetailResponseView,
	type PriceGroupResponseView,
	type PriceTierView,
	type ValidityView,
	type ZoneResponseView,
} from '@mopla/ticketshop-api';

import { deriveTicketCategory } from '../../operatorTickets/hooks';
import { PricingModel } from '../../operatorTickets/types';
import {
	IOperatorTicketDetail,
	IPriceGroupDetail,
	IPriceTier,
	IValidity,
	IZone,
	TicketPeriod,
} from '../types';

interface IUseOperatorTicketDetailResult {
	ticketDetail: IOperatorTicketDetail | null;
	isLoading: boolean;
	error: Error | null;
}

const mapValidity = (dto: ValidityView): IValidity => ({
	period: (dto.period ?? 'week') as TicketPeriod,
	periodCount: dto.periodCount,
	validUntil: dto.validUntil ?? '',
});

const mapZone = (dto: ZoneResponseView): IZone => ({
	id: dto.id ?? '',
	zoneCode: dto.zoneCode ?? '',
	name: dto.name ?? '',
	tarifcode: dto.tarifcode ?? 0,
	neighborIds: dto.neighborIds ?? null,
});

const mapPriceGroup = (dto: PriceGroupResponseView): IPriceGroupDetail => ({
	id: dto.id ?? '',
	displayCode: dto.displayCode ?? '',
	name: dto.name ?? '',
});

const mapPriceTier = (dto: PriceTierView): IPriceTier => ({
	numberOfZones: dto.numberOfZones,
	wholeNetwork: dto.wholeNetwork ? true : undefined,
	priceGroupId: dto.priceGroupId ?? '',
	priceInCents: dto.priceInCents ?? 0,
	variantId: dto.variantId ?? '',
});

const mapOperatorTicketDetail = (
	dto: OperatorTicketDetailResponseView
): IOperatorTicketDetail => ({
	operatorId: dto.operatorId ?? '',
	tariffId: dto.tariffId ?? '',
	ticketId: dto.ticketId ?? '',
	name: dto.name ?? '',
	category: deriveTicketCategory({
		pricingModel: dto.pricingModel,
		period: dto.validity?.period,
		periodCount: dto.validity?.periodCount,
	}),
	pricingModel: (dto.pricingModel ?? 'zone-based') as PricingModel,
	billingMode: dto.billingMode ?? 'single_charge',
	validity: mapValidity(dto.validity ?? {}),
	startDates: dto.startDates,
	zones: (dto.zones ?? []).map(mapZone),
	zoneMapUrl: dto.zoneMapUrl,
	priceGroups: (dto.priceGroups ?? []).map(mapPriceGroup),
	priceGrid: (dto.priceGrid ?? []).map(mapPriceTier),
	stops: dto.stops,
});

/**
 * Step 2a — Zone selection. GET /api/v1/operators/{operatorId}/tickets/{ticketId}
 * BE returns 404 when ticket is not zone-based, not active, or operator has
 * no current tariff. See §7 error cases.
 */
export const useOperatorTicketDetail = (
	operatorId: string | undefined,
	ticketId: string | undefined
): IUseOperatorTicketDetailResult => {
	const { data, isLoading, error } = useQuery({
		...getOperatorTicketDetailOptions({
			path: { operatorId: operatorId ?? '', ticketId: ticketId ?? '' },
		}),
		enabled: !!operatorId && !!ticketId,
		select: mapOperatorTicketDetail,
	});

	return { ticketDetail: data ?? null, isLoading, error };
};
